import { useContext } from 'react';
import { Grid, Button, TextField, MenuItem, ButtonGroup, Chip, Popover, Typography, Checkbox, FormControlLabel, Tooltip, Fab, Zoom, } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MdSearch as SearchIcon, MdClearAll as ClearIcon } from 'react-icons/md';
import { ISearch, ticketsContext } from '../TicketsContext';
import { TicketFilters } from 'helpers/Enums';
import { applicationContext } from 'helpers/services';
import { Autocomplete, FilterContainer, MachineAutocomplete } from 'components';
import { getDynamicContrastColor } from 'helpers';
import { localeManagerContext } from 'helpers/Locale';
import { useIntl } from 'react-intl';
import { LocalizationIds } from 'helpers/Localizations';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import theme from 'assets';


let useStyles = makeStyles()(() => ({
    outlinedPrimary: {
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`
    },
    deleteIconOutlinedColorPrimary: {
        color: theme.palette.primary.main
    },
    outlinedSecondary: {
        color: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.secondary.main}`
    },
    deleteIconOutlinedColorSecondary: {
        color: theme.palette.secondary.dark
    }
}));

let Filters = () => {
    let { formatMessage } = useIntl();
    let { classes } = useStyles();

    let appService = useContext(applicationContext);
    let localeManager = useContext(localeManagerContext);

    let {
        filtersByQueue,
        search,
        defaultSearch,
        anchorEl,
        teamAnchorEl,
        doSearch,
        updateSearch,
        searchCustomers,
        handleTicketCategorySelection,
        handleSubCategorySelection,
        updateAnchorEl,
        updateTeamAnchorEl
    } = useContext(ticketsContext);


    return (
        <FilterContainer onSubmit={() => doSearch(search)}>
            {filtersByQueue.getValueByKey(search.ticketQueue).indexOf(TicketFilters.Ticket) !== -1 &&
                <Grid item>
                    <TextField
                        variant="standard"
                        label={formatMessage({ id: LocalizationIds.Ticket })}
                        value={search.ticketNumber}
                        onChange={(e) => {
                            updateSearch({
                                ...search,
                                ticketNumber: e.target.value
                            });
                        }}
                    />
                </Grid>
            }
            {filtersByQueue.getValueByKey(search.ticketQueue).indexOf(TicketFilters.Customer) !== -1 &&
                <Grid item>
                    <Autocomplete
                        inputLabel={formatMessage({ id: LocalizationIds.Customer })}
                        variant="standard"
                        value={{
                            value: search.customerId,
                            label: search.customerName
                        }}
                        doSearch={(value: string, etag: number) => searchCustomers(value, etag)}
                        onChange={(value: any) => {
                            updateSearch({
                                ...search,
                                customerId: value?.value || 0,
                                customerName: value?.customerName || ''
                            });
                        }}
                    />
                </Grid>
            }
            {filtersByQueue.getValueByKey(search.ticketQueue).indexOf(TicketFilters.Machine) !== -1 &&
                <Grid item>
                    {(search.orderNumber === '' && search.workOrderNumber === '') &&
                        <MachineAutocomplete
                            variant="standard"
                            inputLabel={formatMessage({ id: LocalizationIds.SearchMachine })}
                            onChange={(value) => {
                                let request = { ...search }

                                if (value.linkedTo === "Order")
                                    request = {
                                        ...request,
                                        orderNumber: value.id.orderNumber,
                                        orderType: value.id.orderType
                                    };

                                if (value.linkedTo === "ProductionOrder")
                                    request = {
                                        ...request,
                                        workOrderNumber: value.id.workOrderNumber
                                    };

                                if (search.customerId === 0)
                                    request = {
                                        ...request,
                                        customerId: value.customerId || 0,
                                        customerName: value.customerName || ''
                                    }

                                updateSearch(request);
                            }}
                            customerId={(search.customerId === 0 || search.customerId == null) ? undefined : search.customerId}
                        />
                    }
                    {(search.orderNumber !== '' || search.workOrderNumber !== '') &&
                        <>
                            Ordini:&nbsp;
                            {search.orderNumber !== '' &&
                                <Chip
                                    color="primary"
                                    label={`${search.orderType} ${search.orderNumber}`}
                                    onDelete={() => {
                                        let request = {
                                            ...search,
                                            customerId: 0,
                                            customerName: '',
                                            orderNumber: '',
                                            orderType: ''
                                        }

                                        updateSearch(request);
                                        doSearch(request);
                                    }}
                                />
                            }
                            {search.workOrderNumber !== '' &&
                                <Chip
                                    color="primary"
                                    label={search.workOrderNumber}
                                    onDelete={() => {
                                        let request = {
                                            ...search,
                                            customerId: 0,
                                            customerName: '',
                                            workOrderNumber: ''
                                        }

                                        updateSearch(request);
                                        doSearch(request);
                                    }}
                                />
                            }
                        </>
                    }
                </Grid>
            }

            {filtersByQueue.getValueByKey(search.ticketQueue).indexOf(TicketFilters.Country) !== -1 &&
                <Grid item>
                    <TextField
                        select
                        name="countryCode"
                        label={formatMessage({ id: LocalizationIds.Country })}
                        variant="standard"
                        value={search.countryCode}
                        onChange={(e) => {
                            let request = {
                                ...search,
                                countryCode: e.target.value
                            }

                            updateSearch(request);
                            doSearch(request);
                        }}
                    >
                        <MenuItem value={''}>{formatMessage({ id: LocalizationIds.Any })}</MenuItem>
                        {appService.countries.map(c =>
                            <MenuItem key={c.countryCode} value={c.countryCode}>{c.countryName}</MenuItem>
                        )}
                    </TextField>
                </Grid>
            }
            {filtersByQueue.getValueByKey(search.ticketQueue).indexOf(TicketFilters.Assignee) !== -1 &&
                <Grid item>
                    <TextField
                        select
                        name="filter"
                        variant="standard"
                        label={formatMessage({ id: LocalizationIds.AssignedTo })}
                        value={search.assigneeUserName}
                        onChange={(e) => {
                            let request = {
                                ...search,
                                assigneeUserName: e.target.value
                            }

                            updateSearch(request);
                            doSearch(request);
                        }}
                    >
                        <MenuItem value={''}>{formatMessage({ id: LocalizationIds.Any })}</MenuItem>
                        <MenuItem value={'null'}>{formatMessage({ id: LocalizationIds.NotAssigned })}</MenuItem>
                        {appService.simecUsers
                            .filter(x => x.isDisabled === false)
                            .filter(x => x.simecClaims.canBeAssignedToTicket === true)
                            .map(u => <MenuItem key={u.email} value={u.email}>{u.fullName}</MenuItem>)}
                    </TextField>
                </Grid>}

            {filtersByQueue.getValueByKey(search.ticketQueue).indexOf(TicketFilters.Status) !== -1 &&
                <Grid item xs={2}>
                    <TextField
                        select
                        name='status'
                        label={formatMessage({ id: LocalizationIds.Status })}
                        variant="standard"
                        SelectProps={{ multiple: true }}
                        value={search.status}
                        onChange={(e) => {
                            let request: ISearch = {
                                ...search,
                                status: e.target.value as any
                            }

                            updateSearch(request);
                            doSearch(request);
                        }}
                    >
                        {appService.ticketStatuses
                            .map(u => <MenuItem key={u.code} value={u.code}>{u.descriptions[localeManager.currentLocale]}</MenuItem>)
                        }
                    </TextField>
                </Grid>
            }
            {filtersByQueue.getValueByKey(search.ticketQueue).indexOf(TicketFilters.Jobsite) !== -1 &&
                <Grid item>
                    <Chip
                        label={formatMessage({ id: LocalizationIds.JobSite })}
                        color={
                            ((search.personInTeam != null && search.personInTeam !== '')
                                || search.dateFrom != null
                                || search.dateTo != null)
                                ? "primary"
                                : "secondary"
                        }
                        variant="outlined"
                        classes={{
                            outlinedPrimary: classes.outlinedPrimary,
                            deleteIconOutlinedColorPrimary: classes.deleteIconOutlinedColorPrimary,
                            outlinedSecondary: classes.outlinedSecondary,
                            deleteIconOutlinedColorSecondary: classes.deleteIconOutlinedColorSecondary
                        }}
                        onDelete={() => {
                            let request = {
                                ...search,
                                personInTeam: '',
                                dateFrom: null,
                                dateTo: null
                            }

                            updateSearch(request);
                            doSearch(request);
                        }}
                        onClick={(event: any) => {
                            updateTeamAnchorEl(event.currentTarget);
                        }}
                    />
                    <Popover
                        open={teamAnchorEl !== null}
                        anchorEl={teamAnchorEl}
                        onClose={(e) => updateTeamAnchorEl(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        <div onMouseLeave={(e) => updateTeamAnchorEl(null)}>
                            <Grid container spacing={0}>
                                <Grid item>
                                    <TextField
                                        select
                                        name="filter"
                                        variant="standard"
                                        label={formatMessage({ id: LocalizationIds.TeamMember })}
                                        value={search.personInTeam}
                                        onChange={(e) => {
                                            let request = {
                                                ...search,
                                                personInTeam: e.target.value
                                            }

                                            updateSearch(request);
                                            doSearch(request);
                                        }}
                                    >
                                        <MenuItem value={''}>{formatMessage({ id: LocalizationIds.Any })}</MenuItem>
                                        {appService.simecUsers
                                            .filter(x => x.isDisabled === false)
                                            .filter(x => x.simecClaims.canGoToCustomer === true)
                                            .map(u => <MenuItem key={u.email} value={u.email}>{u.fullName}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item>
                                    <DesktopDatePicker
                                        label={formatMessage({ id: LocalizationIds.JobSiteFrom })}
                                        value={search.dateFrom}
                                        onChange={(value) => {
                                            let newValue = value != null ? moment(value).utc(true).startOf('day') : null;
                                            let request = { ...search, dateFrom: newValue };
                                            updateSearch(request);
                                        }}
                                        renderInput={props => <TextField {...props} variant="standard" />}
                                        inputFormat="DD/MM/YYYY"

                                    />
                                </Grid>
                                <Grid item>
                                    <DesktopDatePicker
                                        label={formatMessage({ id: LocalizationIds.JobSiteTo })}
                                        value={search.dateTo}
                                        onChange={(value) => {
                                            let newValue = value != null ? moment(value).utc(true).startOf('day') : null;
                                            let request = { ...search, dateTo: newValue };
                                            updateSearch(request);
                                        }}
                                        renderInput={props => <TextField {...props} variant="standard" />}
                                        inputFormat="DD/MM/YYYY"

                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </Popover>
                </Grid>
            }

            <Grid item>
                <Tooltip TransitionComponent={Zoom} title={formatMessage({ id: LocalizationIds.ClearFilters })}>
                    <Fab
                        size="small"
                        onClick={() => doSearch({ ...defaultSearch, ticketQueue: search.ticketQueue })}
                    >
                        <ClearIcon size={20} />
                    </Fab>
                </Tooltip>
            </Grid>
            <Grid item>
                <Button
                    type="submit"
                    size="large"
                    color="secondary"
                    variant="outlined"
                    aria-label="search"
                >
                    <SearchIcon />
                </Button>
            </Grid>

            <Grid item xs />
            {filtersByQueue.getValueByKey(search.ticketQueue).indexOf(TicketFilters.Category) !== -1 &&
                <Grid item>
                    <ButtonGroup>
                        {appService.ticketCategories
                            .filter(f => f.subCategories.length > 0)
                            .map(c =>
                                <Button
                                    key={c.ticketCategoryId}
                                    type="button"
                                    color={search.ticketCategoryId === c.ticketCategoryId ? "inherit" : "secondary"}
                                    style={
                                        search.ticketCategoryId === c.ticketCategoryId
                                            ? { margin: 0, backgroundColor: c.color, color: getDynamicContrastColor(c.color) }
                                            : { margin: 0 }
                                    }
                                    variant={search.ticketCategoryId === c.ticketCategoryId ? "contained" : "outlined"}
                                    onClick={() => handleTicketCategorySelection(c.ticketCategoryId)}
                                >
                                    {c.name[localeManager.currentLocale]}
                                </Button>
                            )}
                    </ButtonGroup>

                    {search.ticketCategoryId != null && search.ticketCategoryId !== '' &&
                        <div>
                            <Typography
                                color="primary"
                                variant="subtitle2"
                                onClick={(e) => updateAnchorEl(e.currentTarget)}
                                onMouseEnter={(e) => updateAnchorEl(e.currentTarget)}
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            >
                                {search.ticketSubCategories.length === appService.ticketSubCategories
                                    .filter(f => f.categoryTicketCategoryId === search.ticketCategoryId)
                                    .length &&
                                    <>
                                        {formatMessage({ id: LocalizationIds.AllSubcategoriesSelected })}
                                    </>
                                }
                                {search.ticketSubCategories.length !== appService.ticketSubCategories
                                    .filter(f => f.categoryTicketCategoryId === search.ticketCategoryId)
                                    .length &&
                                    <>
                                        {formatMessage(
                                            { id: LocalizationIds.SelectedSubcategories },
                                            {
                                                count: search.ticketSubCategories.length == 0 ? appService.ticketSubCategories
                                                    .filter(f => f.categoryTicketCategoryId === search.ticketCategoryId)
                                                    .length : search.ticketSubCategories.length
                                            }
                                        )}
                                    </>
                                }
                            </Typography>
                        </div>
                    }

                    <Popover
                        open={anchorEl !== null}
                        anchorEl={anchorEl}
                        onClose={(e) => updateAnchorEl(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        <div style={{ width: 300 }} onMouseLeave={(e) => updateAnchorEl(null)}>
                            <Grid container spacing={0}>
                                {appService.ticketSubCategories
                                    .filter(f => f.categoryTicketCategoryId === search.ticketCategoryId)
                                    .map(subCategory =>
                                        <Grid item xs={12} key={subCategory.id}>
                                            <FormControlLabel
                                                label={subCategory.names[localeManager.currentLocale]}
                                                checked={search.ticketSubCategories.findIndex(f => f === subCategory.id) >= 0}
                                                onChange={() => handleSubCategorySelection(subCategory.id)}
                                                control={<Checkbox color="secondary" style={{ paddingBottom: 0, paddingTop: 0 }} />}
                                            />
                                        </Grid>
                                    )}
                            </Grid>
                        </div>
                    </Popover>
                </Grid>
            }
        </FilterContainer>
    );
}

export default Filters;